import {
  EAR_ANCHOR_INDICES,
  FACE_MESH_CONTOUR_PATHS,
  IRIS_INDICES
} from './featureIndices'
import { expandFaceOvalPoints } from '../faceMaskUtils'

const toPixelPoint = (landmark, width, height, index = -1) => {
  if (!landmark) return null
  return {
    x: landmark.x * width,
    y: landmark.y * height,
    z: landmark.z || 0,
    index
  }
}

const buildPath = (landmarks, indices, width, height) => {
  if (!landmarks?.length || !indices?.length) return []
  const out = []
  for (let i = 0; i < indices.length; i += 1) {
    const idx = indices[i]
    const point = toPixelPoint(landmarks[idx], width, height, idx)
    if (point) out.push(point)
  }
  return out
}

const centroid = (points) => {
  if (!points?.length) return null
  let sx = 0
  let sy = 0
  for (let i = 0; i < points.length; i += 1) {
    sx += points[i].x
    sy += points[i].y
  }
  return {
    x: sx / points.length,
    y: sy / points.length
  }
}

const bounds = (points) => {
  if (!points?.length) return null
  let minX = points[0].x
  let minY = points[0].y
  let maxX = points[0].x
  let maxY = points[0].y
  for (let i = 1; i < points.length; i += 1) {
    const p = points[i]
    if (p.x < minX) minX = p.x
    if (p.y < minY) minY = p.y
    if (p.x > maxX) maxX = p.x
    if (p.y > maxY) maxY = p.y
  }
  return {
    minX,
    minY,
    maxX,
    maxY,
    width: Math.max(0, maxX - minX),
    height: Math.max(0, maxY - minY)
  }
}

const eyeDistancePx = (landmarks, width, height) => {
  const leftEye = landmarks?.[33]
  const rightEye = landmarks?.[263]
  if (!leftEye || !rightEye) return Math.min(width, height) * 0.04
  return Math.hypot(
    (rightEye.x - leftEye.x) * width,
    (rightEye.y - leftEye.y) * height
  )
}

const resolveIrisCenter = (landmarks, centerIdx, ringPath, width, height) =>
  toPixelPoint(landmarks?.[centerIdx], width, height, centerIdx) || centroid(ringPath)

const buildEar = ({
  side,
  anchor,
  top,
  bottom,
  width,
  height,
  baseDist
}) => {
  if (!anchor) return null
  const centerX = anchor.x * width + side * baseDist * 0.12
  const centerY = anchor.y * height
  const verticalSpan =
    top && bottom ? Math.abs(bottom.y - top.y) * height : baseDist * 0.46
  const radiusX = Math.max(10, baseDist * 0.17)
  const radiusY = Math.max(15, verticalSpan * 0.68)
  return {
    center: { x: centerX, y: centerY },
    radiusX,
    radiusY
  }
}

export const extractLandmarkFeatures = ({ landmarks, width, height }) => {
  if (!landmarks?.length || !width || !height) return null

  const paths = {
    faceOval: buildPath(landmarks, FACE_MESH_CONTOUR_PATHS.faceOval, width, height),
    leftEye: buildPath(landmarks, FACE_MESH_CONTOUR_PATHS.leftEye, width, height),
    rightEye: buildPath(landmarks, FACE_MESH_CONTOUR_PATHS.rightEye, width, height),
    leftBrow: buildPath(landmarks, FACE_MESH_CONTOUR_PATHS.leftBrow, width, height),
    rightBrow: buildPath(landmarks, FACE_MESH_CONTOUR_PATHS.rightBrow, width, height),
    noseBridge: buildPath(landmarks, FACE_MESH_CONTOUR_PATHS.noseBridge, width, height),
    lipsOuter: buildPath(landmarks, FACE_MESH_CONTOUR_PATHS.lipsOuter, width, height),
    lipsInner: buildPath(landmarks, FACE_MESH_CONTOUR_PATHS.lipsInner, width, height)
  }
  paths.faceOval = expandFaceOvalPoints(
    paths.faceOval,
    [...paths.leftBrow, ...paths.rightBrow]
  )

  const leftEyeBounds = bounds(paths.leftEye)
  const rightEyeBounds = bounds(paths.rightEye)
  const leftIrisRing = buildPath(landmarks, IRIS_INDICES.leftRing, width, height)
  const rightIrisRing = buildPath(landmarks, IRIS_INDICES.rightRing, width, height)
  const leftIrisCenter = resolveIrisCenter(
    landmarks,
    IRIS_INDICES.leftCenter,
    leftIrisRing,
    width,
    height
  )
  const rightIrisCenter = resolveIrisCenter(
    landmarks,
    IRIS_INDICES.rightCenter,
    rightIrisRing,
    width,
    height
  )

  const eyeDistance = eyeDistancePx(landmarks, width, height)
  const pupilRadius = Math.max(2.4, eyeDistance * 0.065)

  const leftAnchor = landmarks[EAR_ANCHOR_INDICES.leftOuter]
  const rightAnchor = landmarks[EAR_ANCHOR_INDICES.rightOuter]
  const leftTop = landmarks[EAR_ANCHOR_INDICES.leftTop]
  const rightTop = landmarks[EAR_ANCHOR_INDICES.rightTop]
  const leftBottom = landmarks[EAR_ANCHOR_INDICES.leftBottom]
  const rightBottom = landmarks[EAR_ANCHOR_INDICES.rightBottom]
  const baseDist = eyeDistancePx(landmarks, width, height)

  return {
    width,
    height,
    paths,
    face: {
      polygon: paths.faceOval,
      center: centroid(paths.faceOval),
      bounds: bounds(paths.faceOval)
    },
    eyes: {
      left: {
        polygon: paths.leftEye,
        center: centroid(paths.leftEye),
        bounds: leftEyeBounds
      },
      right: {
        polygon: paths.rightEye,
        center: centroid(paths.rightEye),
        bounds: rightEyeBounds
      },
      distance: eyeDistance
    },
    irises: {
      left: {
        ring: leftIrisRing,
        center: leftIrisCenter
      },
      right: {
        ring: rightIrisRing,
        center: rightIrisCenter
      }
    },
    pupils: {
      radius: pupilRadius,
      left: leftIrisCenter
        ? {
            center: leftIrisCenter,
            radius: pupilRadius
          }
        : null,
      right: rightIrisCenter
        ? {
            center: rightIrisCenter,
            radius: pupilRadius
          }
        : null
    },
    ears: {
      left: buildEar({
        side: -1,
        anchor: leftAnchor,
        top: leftTop,
        bottom: leftBottom,
        width,
        height,
        baseDist
      }),
      right: buildEar({
        side: 1,
        anchor: rightAnchor,
        top: rightTop,
        bottom: rightBottom,
        width,
        height,
        baseDist
      })
    }
  }
}

export const FEATURE_PATH_KEYS = [
  'faceOval',
  'leftEye',
  'rightEye',
  'leftBrow',
  'rightBrow',
  'noseBridge',
  'lipsOuter',
  'lipsInner'
]
